import type { Currency } from 'store/accounts/models';
import type { UserStatus } from 'store/auth/types';
import type { MaintenanceWindowModel } from 'store/common/models';
import type { PlanID, ServiceID } from 'store/orders/types';
import type { Nullable, Option, PartialRecord } from 'types';

import type {
  AuthenticationType,
  CountryOption,
  CredentialsGeneratorVersion,
  HostnameRegion,
  IPVersion,
  NetworkType,
  OperationStatus,
  OperationType,
  Protocol,
  ProxyBulkOperationChanges,
  ProxySubnetStatus,
  ProxyType,
} from './types';

// #region Common
export enum ProxyActionModel {
  ACTIVATE = 'ACTIVATE',
  ADMIN_DETAILS = 'ADMIN_DETAILS',
  AUTO_EXTEND = 'AUTO_EXTEND',
  AUTO_EXTEND_TRAFFIC = 'AUTO_EXTEND_TRAFFIC',
  BUY_TRAFFIC = 'BUY_TRAFFIC',
  BUY_THREADS = 'BUY_THREADS',
  BUY_UPLINK_SPEED = 'BUY_UPLINK_SPEED',
  CANCEL = 'CANCEL',
  CHANGE_AUTHENTICATION_METHOD = 'CHANGE_AUTHENTICATION_METHOD',
  CHANGE_HTTPS_PROTOCOL = 'CHANGE_HTTPS_PROTOCOL',
  CHANGE_PORT = 'CHANGE_PORT',
  CHANGE_PROTOCOL = 'CHANGE_PROTOCOL',
  CHANGE_THREADS = 'CHANGE_THREADS',
  CHANGE_UPLINK_SPEED = 'CHANGE_UPLINK_SPEED',
  CHANGE_WHITELIST_IP = 'CHANGE_WHITELIST_IP',
  CREATE_ROUTE = 'CREATE_ROUTE',
  DELETE_ROUTE = 'DELETE_ROUTE',
  DOWNLOAD_CREDENTIALS = 'DOWNLOAD_CREDENTIALS',
  EXTEND_PERIOD = 'EXTEND_PERIOD',
  EXPIRE = 'EXPIRE',
  GENERATE_CREDENTIALS = 'GENERATE_CREDENTIALS',
  INITIALIZE = 'INITIALIZE',
  LIST_EVENTS = 'LIST_EVENTS',
  LIST_HISTORY_CHANGES = 'LIST_HISTORY_CHANGES',
  REACTIVATE = 'REACTIVATE',
  REFUND = 'REFUND',
  REGENERATE_CREDENTIALS = 'REGENERATE_CREDENTIALS',
  REVIVE = 'REVIVE',
  ROTATE_IP = 'ROTATE_IP',
  RUN_DIAGNOSTIC_ROUTINE = 'RUN_DIAGNOSTIC_ROUTINE',
  RUN_TEST = 'RUN_TEST',
  SHOW_DETAILS = 'SHOW_DETAILS',
  ORDERING_SUBNET = 'ORDERING_SUBNET',
  CURL_EXAMPLE = 'CURL_EXAMPLE',
  TRAFFIC_ACCOUNTING = 'TRAFFIC_ACCOUNTING',
  TRAFFIC_USAGE_OVERVIEW = 'TRAFFIC_USAGE_OVERVIEW',
  UPTIME_MONITORING = 'UPTIME_MONITORING',
  CONNECT_WITH_CHROME_EXTENSION = 'CONNECT_WITH_CHROME_EXTENSION',
  CONNECT_WITH_FIREFOX_EXTENSION = 'CONNECT_WITH_FIREFOX_EXTENSION',
  CONNECT_WITH_VPN_APP = 'CONNECT_WITH_VPN_APP',
  DISPLAY_MOBILE_ALERT = 'DISPLAY_MOBILE_ALERT',
  DISPLAY_RESIDENTIAL_ALERT = 'DISPLAY_RESIDENTIAL_ALERT',
}

export type ProxyAdminPricesModel = {
  unitPrice: Nullable<string>;
  totalValue: Nullable<string>;
};

export type ProxyAdminServerModel = {
  id: string;
  name: string;
  ip: string;
};

export type ProxyAdminSubnetModel = {
  id: string;
  subnetCidr: string;
  status: ProxySubnetStatus;
};

export type ProxyAdminVendorModel = {
  urlText: string;
  url: string;
};

export type ProxyAuthenticationModel = {
  whitelistedIps: string[];
  username: Nullable<string>;
  password: Nullable<string>;
};

export type ProxyBandwidthModel = {
  total: Nullable<number>;
  used: Nullable<number>;
  updatedAt: Nullable<string>;
};

export type ProxyConnectionModel = {
  publicIp: Nullable<string>;
  connectIp: Nullable<string>;
  ipVersion: IPVersion;
  lastIp: Nullable<string>;
  httpPort: Nullable<number>;
  httpsPort: Nullable<number>;
  socks5Port: Nullable<number>;
  hostnames: Nullable<Record<string, string>>;
};

export type ProxyLocationModel = {
  country: Nullable<Option<string>>;
  region: Nullable<Option<string>>;
};

export type ProxyRouteModel = {
  id: number;
  proxyId: number;
  publicIp: string;
  port: number;
  serverId: string;
  serverName: string;
};

export enum ProxyStatus {
  PENDING = 'PENDING',
  INITIATING = 'INITIATING',
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  CANCELED = 'CANCELED',
  EXPIRING_SOON = 'EXPIRING_SOON',
}

export enum ProxyStatusChangeFailureReason {
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  ORDER_PENDING = 'ORDER_PENDING',
  INVALID_ORDER = 'INVALID_ORDER',
  NOT_ENOUGH_BALANCE = 'NOT_ENOUGH_BALANCE',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}

export type ProxyTrafficDataModel = {
  date: string;
  value: number;
};

export type ProxyTrafficModel = {
  updatedAt: string;
  data: ProxyTrafficDataModel[];
};

export type ProxyUptimeDataModel = {
  date: string;
  value: Nullable<number>;
};

export type ProxyUptimeModel = {
  updatedAt: string;
  data: ProxyUptimeDataModel[];
};

export enum LimitParameter {
  BANDWIDTH = 'BANDWIDTH',
  SPEED = 'SPEED',
  CONCURRENCY = 'CONCURRENCY',
}

// Technical unit, not localized — passed through as-is, no DTO->Model conversion needed.
export type LimitUnit = 'GB' | 'Mbit' | 'Gbit' | 'connections';

// Priority order (highest first): LIMIT_REACHED > NEAR_CAPACITY > APPROACHING_LIMIT > GROWING_USAGE > NORMAL.
export enum LimitStatus {
  LIMIT_REACHED = 'LIMIT_REACHED',
  NEAR_CAPACITY = 'NEAR_CAPACITY',
  APPROACHING_LIMIT = 'APPROACHING_LIMIT',
  GROWING_USAGE = 'GROWING_USAGE',
  NORMAL = 'NORMAL',
}

// percentage/status are FE-computed — the API only returns raw peak/limit (see PD-3555/PD-3556 BE contract).
// hour is an ISO UTC string, peak === null means no traffic in that hour (not 0).
export type ProxyLimitHourlyModel = {
  hour: string;
  peak: Nullable<number>;
};

export type ProxyLimitModel = {
  parameter: LimitParameter;
  peak: number;
  limit: number;
  unit: LimitUnit;
  percentage: number;
  status: LimitStatus;
  hourly: Nullable<ProxyLimitHourlyModel[]>;
  upgradeAvailable: boolean;
};

// #endregion Common

/**
 * @POST /proxies/bulk/auto-extend/details
 */
export type BulkAutoExtendSettingsModel = {
  isBandwidthRequired: boolean;
  isAutoExtendEnabled: boolean;
};

/**
 * @GET /proxies/{id}/change-authentication-type
 */
export type ChangeAuthenticationTypeModel = {
  currentAuthenticationType: AuthenticationType;
  availableAuthenticationTypes: AuthenticationType[];
};

/**
 * @GET /proxies/{id}/change-protocol
 */
export type ChangeProtocolModel = {
  currentType: Protocol;
  availableTypes: Protocol[];
};

/**
 * @GET /proxies/{id}/change-threads
 */
export type ChangeThreadsModel = {
  currentThreads: number;
  options: number[];
};

/**
 * @GET /proxies/{id}/change-uplink-speed
 */
export type ChangeUplinkSpeedModel = {
  currentUplinkSpeed: number;
  options: number[];
};

/**
 * @POST /proxies/{id}/admin/diagnostic-routine
 */
export type DiagnosticRoutineModel = {
  url: string;
  statusCode: number;
  statusMessage: string;
  time: number;
  metadata: Record<string, string>;
};

/**
 * @GET /proxies/settings/global-ip-whitelist
 * @GET /proxies/{id}/whitelist-ip
 */
export type IPWhitelistModel = {
  whitelistedIps: string[];
  allowedWhitelistIpCount: number;
};

/**
 * @GET /proxies/{id}/credential-generator
 */
type CredentialsModel = {
  hostnameIp: string;
  networkType: NetworkType;
  version: CredentialsGeneratorVersion;
  region: HostnameRegion;
};

type LocationsModel = {
  countries: Array<Option<string>>;
  states: Record<string, Array<Option<string>>>;
  cities: Record<string, Record<string, Array<Option<string>>>>;
};

export type CredentialsGeneratorModel = {
  credentials: CredentialsModel[];
  locations: LocationsModel;
};

/**
 * @GET /services/proxies
 */
export type ProxyModel = {
  id: number;
  status: ProxyStatus;
  networkType: NetworkType;
  authentication: ProxyAuthenticationModel;
  connection: ProxyConnectionModel;
  proxyType: Protocol;
  createdAt: string;
  expiresAt: Nullable<string>;
  serviceId: Nullable<ServiceID>;
  planId: Nullable<PlanID>;
  price: Nullable<number>;
  ispName: Nullable<string>;
  bandwidth: ProxyBandwidthModel;
  uplinkSpeed: Nullable<number>;
  threads: Nullable<number>;
  location: ProxyLocationModel;
  note: Nullable<string>;
  actions: ProxyActionModel[];
  maintenanceWindows: MaintenanceWindowModel[];
};

/**
 * @GET /proxies/{id}/admin/details
 */
export type ProxyAdminDetailsModel = {
  userName: string;
  userId: number;
  userStatus: UserStatus;
  proposition: Nullable<string>;
  reservedIp: Nullable<string>;
  server: Nullable<ProxyAdminServerModel>;
  subnet: Nullable<ProxyAdminSubnetModel>;
  isUsed: boolean;
  isHttpsProtocolSupported: boolean;
  vendor: ProxyAdminVendorModel[];
  prices: Nullable<ProxyAdminPricesModel>;
  routes: ProxyRouteModel[];
  type: PlanID;
  statusChangeFailureReason: Nullable<ProxyStatusChangeFailureReason>;
};

/**
 *  @GET /proxies/{id}/change-history
 */
export type ProxyChangelogModel = {
  id: string;
  oldValue: string;
  newValue: string;
  createdAt: string;
  fieldName: string;
  changedBy: Nullable<string>;
  userId: Nullable<number>;
};

/**
 * @POST /proxies/bulk/extend-period/price
 * @POST /proxies/bulk/buy-traffic/price
 * @POST /proxies/{id}/period-extension-price
 * @POST /proxies/{id}/bandwidth-price
 * @POST /proxies/{id}/reactivate/price
 */
export type ProxyExtensionPriceModel = {
  finalPrice: number;
  priceNoDiscounts: number;
  discount: number;
  unitPrice: number;
  additionalAmount: number;
  additionalAmountAfterDiscount: number;
  paymentFee: number;
  subtotal: number;
  discountAmount: number;
  finalPriceInCurrency: number;
  currency: Nullable<Currency>;
};

/**
 * @GET /proxies/{id}/events
 */
export type ProxyEventModel = {
  id: string;
  createdAt: string;
  message: string;
};

/**
 * @GET /services/proxies/isp
 */
export type ProxyISPModel = {
  id: string;
  name: string;
};

/**
 * @GET /services/proxies/{id}
 */
export type ProxyOverviewModel = ProxyModel & {
  plan: Nullable<PlanID>;
  activatedAt: Nullable<string>;
  canceledAt: Nullable<string>;
  updatedAt: Nullable<string>;
  isAutoExtendEnabled: boolean;
  autoExtendBandwidthAmount: Nullable<number>;
  autoExtendAt: Nullable<string>;
  routes: ProxyRouteModel[];
  traffic: ProxyTrafficModel;
  uptime: ProxyUptimeModel;
  type: string;
  currentUsage: ProxyLimitModel[];
};

/**
 * @GET /services/proxies/limit-signals
 */
export type ProxyLimitSignalModel = {
  proxyId: number;
  ip: string;
  countryCode: Nullable<string>;
  networkType: NetworkType;
  note: Nullable<string>;
  limit: ProxyLimitModel;
};

export type ProxyLimitSignalsModel = {
  items: ProxyLimitSignalModel[];
  updatedAt: Nullable<string>;
};

/**
 * @GET /services/proxies/replacement/list
 */
export type ProxyReplacementDetailsModel = {
  id: number;
  status: ProxyStatus;
  networkType: NetworkType;
  publicIp: string;
  createdAt: string;
  expiresAt: string;
  ispId: string;
  countryCode: string;
  note: Nullable<string>;
  isUnusedProxy: boolean;
  ipVersion: Nullable<IPVersion>;
};

/**
 * @POST /order/configuration
 */
export type ProxyReplacementOptions = {
  country: CountryOption[];
  isps: ISPOption[];
};

export type ProxyReplacementOptionsModel = PartialRecord<
  NetworkType,
  PartialRecord<ProxyType, ProxyReplacementOptions>
>;

/**
 * @GET /proxies/{id}/admin/routes/details
 */
export type ProxySubnetModel = {
  id: string;
  label: string;
  countryCode: string;
};

/**
 * @GET /proxies/summary
 */
export type ProxySummaryModel = {
  active: number;
  expiring: number;
  inactive: number;
};

/**
 * @GET /proxies/{id}/buy-threads
 */
export type UpgradeThreadsPriceModel = {
  thread: number;
  price: number;
};

/**
 * @GET /proxies/{id}/buy-uplink-speed
 */
export type UpgradeUplinkSpeedPriceModel = {
  mbit: number;
  price: number;
};

export type ISPOption = {
  id: string;
  name: string;
  country: string;
  amount: string;
};

/**
 * @POST /proxies/bulk/extend-period
 */
export type BulkOperationModel = {
  operationId: string;
};

/**
 * @POST /bulk-operations/{operationId}
 */
export type ProxyBulkOperationModel = {
  id: string;
  type: OperationType;
  changes: ProxyBulkOperationChanges;
  status: OperationStatus;
  total: number;
  processed: number;
  failureReasons: string[];
};

/**
 * @GET /bulk-operations/{operationId}/tasks
 */
export type ProxyBulkOperationTaskModel = {
  id: number;
  note: Nullable<string>;
  publicIp: string;
  networkType: NetworkType;
  expiresAt: string;
  protocol: Protocol;
  authType: AuthenticationType;
  isAutoExtendable: boolean;
  traffic: Nullable<number>;
  type: 'proxy';
  reason: string;
};
